import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "../../components/TopBar";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { fetchCategories, listBranches } from "../../lib/api";
import { MdCloudUpload, MdDelete } from "react-icons/md";
import { createProduct } from "../../lib/api";
import { useBranchFilter } from "../../hooks/useBranchFilter";
import { useAuth } from "../../contexts/AuthContext";

const ProductNew = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const { branchId, isAdmin } = useBranchFilter();
  const { user } = useAuth();
  const [product, setProduct] = useState({
    name: "",
    price: "",
    minAuctionAmount: "",
    oldPrice: "",
    description: "",
    stock: "",
    category_id: null,
    branch_id: branchId || "",
    auction_date: new Date().toISOString().split('T')[0],
    status: "live",
    images: [],
    reviews: []
  });

  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [cats, branchList] = await Promise.all([
          fetchCategories(),
          listBranches()
        ]);
        
        setCategories(cats);
        setBranches(branchList);
        
        if (cats?.length) {
          setProduct((p) => ({ ...p, category_id: cats[0].id }));
        }
        
        // If branch manager, use their branch; if admin, default to first branch
        if (branchId) {
          setProduct((p) => ({ ...p, branch_id: branchId }));
        } else if (branchList?.length) {
          setProduct((p) => ({ ...p, branch_id: branchList[0].id }));
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load categories and branches', e);
      }
    })();
  }, [branchId]);

  const [review, setReview] = useState({
    username: "",
    stars: 5,
    comment: ""
  });

  const [imagePreviews, setImagePreviews] = useState([]);

  const handleAddReview = (e) => {
    e.preventDefault();
    if (!review.username || !review.comment) {
      toast.error("Please fill in all review fields");
      return;
    }
    setProduct({
      ...product,
      reviews: [...product.reviews, { ...review }]
    });
    setReview({
      username: "",
      stars: 5,
      comment: ""
    });
    toast.success("Review added successfully");
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    
    // Preview images
    const newImagePreviews = files.map(file => ({
      url: URL.createObjectURL(file),
      file: file
    }));
    
    setImagePreviews([...imagePreviews, ...newImagePreviews]);
    setProduct({
      ...product,
      images: [...product.images, ...files]
    });
  };

  const removeImage = (index) => {
    const newPreviews = imagePreviews.filter((_, i) => i !== index);
    const newImages = product.images.filter((_, i) => i !== index);
    
    // Revoke the URL to prevent memory leaks
    URL.revokeObjectURL(imagePreviews[index].url);
    
    setImagePreviews(newPreviews);
    setProduct({
      ...product,
      images: newImages
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const created = await createProduct({
        name: product.name,
        price: Number(product.price) || 0,
        minAuctionAmount: Number(product.minAuctionAmount) || null,
        oldPrice: product.oldPrice ? Number(product.oldPrice) : null,
        description: product.description,
        stock: Number(product.stock) || 0,
        category_id: product.category_id,
        branch_id: product.branch_id,
        auction_date: product.auction_date,
        status: product.status,
      });
      toast.success("Product created successfully");
      navigate(`/bids`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      toast.error(err?.message || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <TopBar />
      <div className="flex-1 overflow-y-auto bg-gray-50">
        <div className="container mx-auto px-4 py-6">
          <div className="bg-white rounded-lg shadow-md">
            <div className="p-6">
              <h1 className="text-2xl font-bold mb-6">Create New Product</h1>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Info Section */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Name
                      </label>
                      <input
                        type="text"
                        value={product.name}
                        onChange={(e) => setProduct({ ...product, name: e.target.value })}
                        className="w-full px-3 py-2 border rounded"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Category
                      </label>
                      <select
                        className="w-full p-2 border rounded-md"
                        value={product.category_id || ''}
                        onChange={(e) => setProduct({ ...product, category_id: Number(e.target.value) })}
                        required
                      >
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Price and Stock Section */}
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          Price
                        </label>
                        <input
                          type="number"
                          value={product.price}
                          onChange={(e) => setProduct({ ...product, price: Number(e.target.value) })}
                          className="w-full px-3 py-2 border rounded"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          Minimum Auction Amount
                        </label>
                        <input
                          type="number"
                          value={product.minAuctionAmount}
                          onChange={(e) => setProduct({ ...product, minAuctionAmount: Number(e.target.value) })}
                          className="w-full px-3 py-2 border rounded"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          Old Price
                        </label>
                        <input
                          type="number"
                          value={product.oldPrice}
                          onChange={(e) => setProduct({ ...product, oldPrice: Number(e.target.value) })}
                          className="w-full px-3 py-2 border rounded"
                          placeholder="(Optional)"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Stock
                      </label>
                      <input
                        type="number"
                        value={product.stock}
                        onChange={(e) => setProduct({ ...product, stock: Number(e.target.value) })}
                        className="w-full px-3 py-2 border rounded"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Description
                      </label>
                      <textarea
                        value={product.description}
                        onChange={(e) => setProduct({ ...product, description: e.target.value })}
                        className="w-full px-3 py-2 border rounded"
                        rows="4"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Auction & Branch Details Section */}
                <div className="mt-8 border-t pt-6">
                  <h2 className="text-xl font-bold mb-4 text-gray-800">Auction & Branch Details</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Branch Selection */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Branch <span className="text-red-500">*</span>
                      </label>
                      <select
                        className="w-full p-2 border rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
                        value={product.branch_id || ''}
                        onChange={(e) => setProduct({ ...product, branch_id: e.target.value })}
                        required
                        disabled={!isAdmin} // Branch managers can't change branch
                      >
                        <option value="">Select Branch</option>
                        {branches.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.branch_name || branch.name}
                          </option>
                        ))}
                      </select>
                      {!isAdmin && (
                        <p className="text-xs text-gray-500 mt-1">
                          You can only add products to your assigned branch
                        </p>
                      )}
                    </div>

                    {/* Auction Date */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Auction/Bidding Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={product.auction_date}
                        onChange={(e) => setProduct({ ...product, auction_date: e.target.value })}
                        className="w-full px-3 py-2 border rounded"
                        required
                      />
                    </div>

                    {/* Status */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Bid Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        className="w-full p-2 border rounded-md"
                        value={product.status}
                        onChange={(e) => setProduct({ ...product, status: e.target.value })}
                        required
                      >
                        <option value="live">🟢 Live Bids</option>
                        <option value="scheduled">🔵 Coming Bids (Upcoming)</option>
                        <option value="completed">⚫ Past Bids (Completed)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        This determines which tab the product appears in
                      </p>
                    </div>
                  </div>
                </div>

                {/* Image Upload Section */}
                <div className="mt-8">
                  <h2 className="text-xl font-bold mb-4">Product Images</h2>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="hidden"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                      />
                      <MdCloudUpload className="mx-auto text-4xl text-gray-400 mb-2" />
                      <p className="text-gray-600">Click to upload product images</p>
                      <p className="text-sm text-gray-500 mt-1">PNG, JPG up to 5MB</p>
                    </div>

                    {/* Image Previews */}
                    {imagePreviews.length > 0 && (
                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                        {imagePreviews.map((preview, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={preview.url}
                              alt={`Preview ${index + 1}`}
                              className="w-full h-32 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(index)}
                              className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <MdDelete />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Review Section */}
                <div className="mt-8">
                  <h2 className="text-xl font-bold mb-4">Add Product Review</h2>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          Username
                        </label>
                        <input
                          type="text"
                          value={review.username}
                          onChange={(e) => setReview({ ...review, username: e.target.value })}
                          className="w-full px-3 py-2 border rounded"
                          placeholder="Enter username"
                        />
                      </div>

                      <div>
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          Rating (Stars)
                        </label>
                        <select
                          value={review.stars}
                          onChange={(e) => setReview({ ...review, stars: Number(e.target.value) })}
                          className="w-full px-3 py-2 border rounded"
                        >
                          {[1, 2, 3, 4, 5].map((num) => (
                            <option key={num} value={num}>
                              {num} {num === 1 ? 'Star' : 'Stars'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Review Comment
                      </label>
                      <textarea
                        value={review.comment}
                        onChange={(e) => setReview({ ...review, comment: e.target.value })}
                        className="w-full px-3 py-2 border rounded"
                        rows="3"
                        placeholder="Enter review comment"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleAddReview}
                      className="mt-4 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
                    >
                      Add Review
                    </button>
                  </div>

                  {/* Display added reviews */}
                  {product.reviews.length > 0 && (
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold mb-2">Added Reviews:</h3>
                      <div className="max-h-60 overflow-y-auto space-y-2">
                        {product.reviews.map((r, index) => (
                          <div key={index} className="bg-gray-100 p-3 rounded">
                            <div className="font-medium">{r.username}</div>
                            <div className="text-yellow-500">{r.stars} Stars</div>
                            <div className="text-gray-600">{r.comment}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                  >
                    {loading ? "Creating..." : "Create Product"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductNew;
